'use client';

import { useMemo, type ComponentProps } from 'react';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { ManualControlHold } from '@/types/coordination';
import type { AlarmEntry } from '@/components/dashboard/alarmTypes';
import { StripBoard } from '@/components/dashboard/StripBoard';
import { stageInsetsForHost, stageTopAnchoredMaxHeight } from './CopStageInsets';
import { useEnclosingStageInsets } from './useStageInsets';
import { DroneMap, type GeofenceOverlay, type RcController, type SiteMarker, type DockMarker, type PlanRoutePoint, type TriggerMarker } from '@/components/dashboard/DroneMap';
import type { DroneMapProps, DroneSelectSource } from '@/components/dashboard/droneMapTypes';
import type { ActiveMission } from './types';
import type { MapMode } from '@/lib/map-mode';

interface CopMapStageProps {
  drones: LiveDrone[];
  selectedDroneId: string | null;
  /** Forwarded to BOTH children that select — the map and the strip board — so
   *  `source` is what tells them apart downstream. The strip board passes none:
   *  reading a strip is not asking to see the aircraft's camera. */
  onSelectDrone: (id: string, source?: DroneSelectSource) => void;
  /**
   * Who is hand-flying what, org-wide. Handed to BOTH the strip board and the
   * map, which are the two places an operator reads the air picture from — the
   * mark has to be in whichever one they happen to be scanning.
   */
  manualHolds?: ReadonlyMap<string, ManualControlHold>;
  /**
   * Read here by `StripBoard` alone, to mark the rows of drones that have one.
   *
   * The alert SURFACE is not on this stage any more: acknowledge, resolve and
   * fly-to left with it when the chip became the top row's bell
   * (`CopAlertBell`), so this stage no longer carries three callbacks it only
   * ever forwarded.
   */
  alerts: AlarmEntry[];
  cooperativeTraffic: ComponentProps<typeof DroneMap>['cooperativeTraffic'];
  geofences: GeofenceOverlay[];
  sites: SiteMarker[];
  missions: ActiveMission[];
  selectedMissionId?: string | null;
  hoveredMissionId?: string | null;
  onSelectMission?: (missionId: string) => void;
  /** Coverage handovers in progress, for the map's line between the two aircraft. */
  handoverLinks?: DroneMapProps['handoverLinks'];
  docks?: DockMarker[];
  selectedDockId?: string | null;
  onSelectDock?: (id: string) => void;
  triggers?: TriggerMarker[];
  onDispatchFromTrigger?: (trigger: TriggerMarker) => void;
  onDismissTrigger?: (id: string) => void;
  /** Approve the aircraft the platform proposed for a trigger, and launch it (#253). */
  onApproveTrigger?: (id: string) => void | Promise<void>;
  /** Refuse the proposed aircraft while leaving the detection on the board. */
  onRejectTrigger?: (id: string) => void;
  connected: boolean;
  /**
   * The COP's one armed map mode, read-only here.
   *
   * Arming and disarming left with the zones panel in slice 8: the page owns
   * the mode and now owns the only surface in this subtree that used to change
   * it, so the stage passes the mode to the map and asks for nothing.
   */
  mapMode: MapMode;
  onDispatchTargetPicked: (lat: number, lng: number) => void;
  onRequestDispatchHere?: (lat: number, lng: number) => void;
  onGotoPointPicked?: (lat: number, lng: number) => void;
  /** Receives the drone id the context menu acted on — the map passes it, so
   *  dropping it here would silently open video for the wrong aircraft. */
  onOpenVideo: (droneId: string) => void;
  /** Opens the zones sheet from the map's Layers menu. Absent for a viewer. */
  onOpenZones?: () => void;
  /** The VIDEO key beside FOLLOW and TRACK. Null and no key is drawn (the
   *  deployment switch is off, or a manual session owns the stage). */
  videoBeside?: DroneMapProps['videoBeside'];
  /**
   * The shape currently being drawn, for the map to paint.
   *
   * One object rather than six props, because it is one subject and it is not
   * this component's. The state lives on the page (`_cop/zoneAuthoring.ts`)
   * since slice 8 made the zones panel the right column's fourth tab: the panel
   * and the drawn shape are in different subtrees now, and the only way they
   * cannot disagree is by reading the same reducer.
   */
  zoneDraw: {
    points: [number, number][];
    polygonClosed: boolean;
    circleCenter: { lat: number; lng: number } | null;
    circleRadius: number;
    color: string;
    onMapClick: (lat: number, lng: number) => void;
  };
  planWaypoints?: PlanRoutePoint[];
  /** 022. Radius of the ORBIT ring to draw around the single placed point, in metres. */
  planOrbitRadiusM?: number | null;
  planHome?: { lat: number; lng: number } | null;
  /** Banner copy for the armed mode; the page derives it from the mode. */
  planModeLabel?: string | null;
  planEditable?: boolean;
  onPlanModeExit?: () => void;
  planSelectedIndex?: number | null;
  planActiveIndex?: number | null;
  onPlanMapClick?: (lat: number, lng: number) => void;
  onPlanWaypointClick?: (index: number) => void;
  onPlanWaypointDrag?: (index: number, lat: number, lng: number) => void;
  /** Dock-launch step overlay (target/altitude/review) — floats over the map
   *  viewport only, never a second sidebar column. */
  launchOverlay?: React.ReactNode;
  /**
   * Manual flight control surface. Rendered over the whole map viewport, and
   * itself `pointer-events: none` except for its three controls — so with manual
   * mode armed the map is still pannable everywhere the operator is not
   * actually flying from. Rendered LAST so it sits above the launch overlay,
   * which is the one thing that must not swallow a flight gesture.
   */
  manualOverlay?: React.ReactNode;
  /**
   * Strip everything that is not the map and the flight surface.
   *
   * For the manual-flight control minimap (280 px square, VIDEO layout): the
   * strip board, the weather panel and the map's own key column are each wider
   * than a usable share of that box, and none of them is what the operator is
   * looking at it for. The COP's full chrome comes back the moment the layout
   * does.
   */
  compact?: boolean;
  /** The selection chip, anchored to the selected drone's marker by the map. */
  droneCard?: DroneMapProps['selectedDroneOverlay'];
}

/** Map column: strip board overlay, zones overlay and the map itself. */
export function CopMapStage({
  drones,
  manualHolds,
  selectedDroneId,
  onSelectDrone,
  alerts,
  cooperativeTraffic,
  geofences,
  sites,
  missions,
  selectedMissionId,
  hoveredMissionId,
  onSelectMission,
  handoverLinks,
  docks = [],
  selectedDockId,
  onSelectDock,
  triggers,
  onDispatchFromTrigger,
  onDismissTrigger,
  onApproveTrigger,
  onRejectTrigger,
  connected,
  mapMode,
  onDispatchTargetPicked,
  onRequestDispatchHere,
  onGotoPointPicked,
  onOpenVideo,
  onOpenZones,
  videoBeside = null,
  zoneDraw,
  planWaypoints,
  planOrbitRadiusM,
  planHome,
  planModeLabel,
  planEditable,
  onPlanModeExit,
  planSelectedIndex,
  planActiveIndex,
  onPlanMapClick,
  onPlanWaypointClick,
  onPlanWaypointDrag,
  launchOverlay,
  manualOverlay,
  compact = false,
  droneCard,
}: CopMapStageProps) {
  // The AIR count is NOT derived here any more — `StripBoard` computes it from the same pass that
  // buckets its rows, under its own 1 Hz tick. This component has no ticker and re-renders only on
  // an inbound telemetry frame, so any freshness rule applied here would freeze for exactly the
  // aircraft it exists to catch: the one that has stopped sending. The header would read AIR 1 over
  // a board whose rows had already gone OFFLINE.
  //
  // This list stays, and stays status-only. It feeds map behaviour that follows aircraft the
  // operator has been watching, where dropping one the moment its link goes quiet is the wrong
  // move — it is the moment you most want the map to keep showing where it was.
  const flyingIds: string[] = [];
  for (const d of drones) {
    if (d.status === 'flying') flyingIds.push(d.drone_id);
  }
  const flyingDroneIdsKey = flyingIds.join(',');

  // RC controller positions: NONE, until a real source exists.
  //
  // This used to synthesise them - it took each flying drone's position, shifted
  // it ~670 m, and added a hash-derived pseudo-random offset, then drew that on
  // the COP as an asset. Nothing in the platform reports where a ground
  // controller actually is (no telemetry field, no backend source), so every RC
  // marker an operator ever saw was invented by the browser.
  //
  // A tactical display must never show a position the system does not know. An
  // operator could vector someone to it, or read it as the pilot's location.
  // Showing nothing is correct; the real fix is the companion app reporting its
  // own GPS, at which point this reads that data instead.
  const rcControllers = useMemo<RcController[]>(() => [], []);

  /**
   * What floats over this stage, so the map's own bottom chrome can get out from
   * under it.
   *
   * The map key, the scale bar, the airspace panel and the weather panel all sit
   * at fixed offsets from the map's bottom edge, at `z-10`/`z-20`, and the bottom
   * stack is `z-30`. Wind is a go/no-go input for a launch and the operator
   * cannot drag a floating bar off it, so they are lifted rather than covered.
   *
   * Zero in `compact`: the 280 px control minimap is lifted clear of the stack by
   * `ManualStage`, and it carries none of this chrome anyway.
   */
  // The ENCLOSING box, not the stage. In SPLIT this whole component is the map
  // half, whose right edge is where the picture begins rather than where the
  // side panel is - so the stage's right inset does not apply to it, and
  // `ManualStage` is the only thing that knows that. Outside a `ManualStage`
  // this falls back to the stage's own insets, which is the right answer when
  // the box IS the stage.
  const chromeInsets = stageInsetsForHost(compact, useEnclosingStageInsets());

  return (
    <div className="flex-1 min-h-0 min-w-0 relative">
      {/* Left rail: the strip board, and nothing above it any more — the alert
          chip that used to sit at its head is the top row's bell now, so the
          board starts at the top of the stage. The wrapper is `absolute` but
          carries NO z-index of its own, so the board competes with the map's
          own overlays exactly as it did. `pointer-events-none` keeps the gap
          around it draggable map. */}
      <div
        className="absolute top-2 left-2 flex flex-col items-start gap-2 pointer-events-none"
        style={{
          // Capped clear of the bottom stack, or the last strip-board rows sit
          // behind the command bar. See `stageTopAnchoredMaxHeight`.
          maxHeight: stageTopAnchoredMaxHeight(chromeInsets.bottom, chromeInsets.top),
          // And clear of the navigation rail, which floats over the stage's left
          // edge on the COP. The board's rows are real buttons carrying
          // `aria-pressed`, so leaving them under it hides aircraft from the
          // roster and fails WCAG 2.4.11 for anyone reaching them by keyboard.
          ...(chromeInsets.left > 0
            ? { left: `calc(0.5rem + ${chromeInsets.left}px)` }
            : null),
          // And clear of the TOP stack, which since #505 slice 7 holds the dock
          // roster and the manual session strip. Both used to be rows ABOVE the
          // map, so the board's top edge was never contested; now they float over
          // it at `z-30` against this wrapper's no z-index at all, so without
          // this the roster is drawn over the first strip-board rows on every
          // load - the same rows, and the same WCAG 2.4.11 failure, the left
          // inset above exists for.
          ...(chromeInsets.top > 0
            ? { top: `calc(0.5rem + ${chromeInsets.top}px)` }
            : null),
        }}
      >
        {!compact && (
        <div className="hidden lg:flex relative z-10 min-h-0 overflow-y-auto pointer-events-auto">
          <StripBoard
          drones={drones}
          alerts={alerts}
          selectedDroneId={selectedDroneId}
          onSelectDrone={(id) => {
            onSelectDrone(id);
            const drone = drones.find((d) => d.drone_id === id);
            if (drone) {
              window.dispatchEvent(
                new CustomEvent('flyToDrone', { detail: { lat: drone.latitude, lng: drone.longitude } }),
              );
            }
          }}
          manualHolds={manualHolds}
        />
        </div>
        )}
      </div>
      {!connected && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="surface-chrome rounded-lg px-4 py-3 flex items-center gap-2">
            <div className="size-3 rounded-full border-2 border-border border-t-primary animate-spin" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Connecting to telemetry feed…</span>
          </div>
        </div>
      )}
      {planModeLabel && (
        <div
          className="absolute top-2 left-1/2 z-10 -translate-x-1/2 rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 shadow-lg backdrop-blur-sm"
          // Centred on the stage's top edge, which the top stack now covers.
          // This label says which plan mode the next map click belongs to, so a
          // copy of it hidden behind the dock roster is an operator clicking the
          // map without knowing what the click will do.
          style={chromeInsets.top > 0 ? { top: `calc(0.5rem + ${chromeInsets.top}px)` } : undefined}
        >
          <span className="text-[10px] uppercase tracking-wide text-primary">{planModeLabel}</span>
        </div>
      )}
      <DroneMap
        drones={drones}
        selectedDroneId={selectedDroneId}
        manualHolds={manualHolds}
        onSelectDrone={onSelectDrone}
        selectedDroneOverlay={droneCard}
        cooperativeTraffic={cooperativeTraffic}
        geofences={geofences}
        controllers={rcControllers}
        sites={sites}
        missions={missions}
        selectedMissionId={selectedMissionId}
        hoveredMissionId={hoveredMissionId}
        onSelectMission={onSelectMission}
        handoverLinks={handoverLinks}
        docks={docks}
        selectedDockId={selectedDockId}
        onSelectDock={onSelectDock}
        triggers={triggers}
        onDispatchFromTrigger={onDispatchFromTrigger}
        onDismissTrigger={onDismissTrigger}
        onApproveTrigger={onApproveTrigger}
        onRejectTrigger={onRejectTrigger}
        mapMode={mapMode}
        onDispatchTargetPicked={onDispatchTargetPicked}
        onRequestDispatchHere={onRequestDispatchHere}
        onGotoPointPicked={onGotoPointPicked}
        onOpenVideo={onOpenVideo}
        onOpenZones={onOpenZones}
        videoBeside={videoBeside}
        zoneDrawPoints={zoneDraw.points}
        zonePolygonClosed={zoneDraw.polygonClosed}
        onZoneMapClick={zoneDraw.onMapClick}
        zoneCircleCenter={zoneDraw.circleCenter}
        zoneCircleRadius={zoneDraw.circleRadius}
        zoneDrawColor={zoneDraw.color}
        planWaypoints={planWaypoints}
        planOrbitRadiusM={planOrbitRadiusM}
        planHome={planHome}
        planEditable={planEditable}
        onPlanModeExit={onPlanModeExit}
        planSelectedIndex={planSelectedIndex}
        planActiveIndex={planActiveIndex}
        onPlanMapClick={onPlanMapClick}
        onPlanWaypointClick={onPlanWaypointClick}
        onPlanWaypointDrag={onPlanWaypointDrag}
        compact={compact}
        chromeInsets={chromeInsets}
      />
      {launchOverlay}
      {manualOverlay}
    </div>
  );
}
